/**
 * Page de détail d'un utilisateur
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Shield, Lock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { userAdminApi } from '../../infrastructure/api';
import type { UserAdminDTO } from '../../application/dto/dtos';

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserAdminDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    userAdminApi.obtenirUtilisateur(id)
      .then((response) => {
        if (!response.succes) {
          setError(response.message || 'Utilisateur introuvable');
          return;
        }
        const utilisateur = (response as any)?.data?.utilisateur;
        if (utilisateur) {
          setUser(utilisateur);
        } else {
          setError('Données utilisateur manquantes');
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center gap-2 text-content-muted hover:text-content"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la liste
        </button>
        <div className="bg-danger-light border border-danger rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0" />
          <p className="text-danger">{error || 'Utilisateur introuvable'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/users')}
          className="flex items-center gap-2 text-content-muted hover:text-content transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>
        <h1 className="text-2xl font-bold text-content">
          Détail utilisateur
        </h1>
      </div>

      <div className="bg-surface rounded-lg shadow-md p-6 space-y-6">
        {/* En-tête */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-accent-light flex items-center justify-center">
            <User className="w-8 h-8 text-accent" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-content">
              {user.prenom} {user.nom}
            </h2>
            <p className="text-sm text-content-subtle">{user.email}</p>
          </div>
          <div className="ml-auto flex gap-2">
            {user.isActive ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-success-light text-success">
                <CheckCircle className="w-4 h-4" />
                Actif
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-surface-overlay text-content-muted">
                <XCircle className="w-4 h-4" />
                Inactif
              </span>
            )}
            {user.estVerrouille && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-danger-light text-danger">
                <Lock className="w-4 h-4" />
                Verrouillé
              </span>
            )}
          </div>
        </div>

        {/* Informations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border">
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-content-subtle uppercase tracking-wider">
              Informations générales
            </h3>
            <InfoRow icon={<Mail className="w-4 h-4" />} label="Email" value={user.email} />
            <InfoRow icon={<User className="w-4 h-4" />} label="Prénom" value={user.prenom || '—'} />
            <InfoRow icon={<User className="w-4 h-4" />} label="Nom" value={user.nom || '—'} />
            <InfoRow icon={<Shield className="w-4 h-4" />} label="Rôle" value={user.role || '—'} />
          </div>
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-content-subtle uppercase tracking-wider">
              Sécurité
            </h3>
            <InfoRow icon={<Shield className="w-4 h-4" />} label="2FA" value={user.deuxFacteursActive ? 'Activé' : 'Désactivé'} />
            <InfoRow icon={<Lock className="w-4 h-4" />} label="Compte verrouillé" value={user.estVerrouille ? 'Oui' : 'Non'} />
            {user.dateCreation && (
              <InfoRow icon={<User className="w-4 h-4" />} label="Créé le" value={new Date(user.dateCreation).toLocaleDateString('fr-FR')} />
            )}
            {user.dateDerniereConnexion && (
              <InfoRow icon={<User className="w-4 h-4" />} label="Dernière connexion" value={new Date(user.dateDerniereConnexion).toLocaleDateString('fr-FR')} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-content-subtle">{icon}</span>
      <span className="text-sm text-content-subtle w-36 flex-shrink-0">{label}</span>
      <span className="text-sm font-medium text-content">{value}</span>
    </div>
  );
}

export default UserDetailPage;
